import type { UserView, ConditionDuration } from '../../../types';
import type {
	EvidenceSource,
	Group,
	GroupMetadata,
	GroupRiskModel,
	GroupRiskModelMetadata,
	HccVersions,
	RiskModelData,
	RiskModelGroupState,
	Suggestion as SuggestionCore,
	Diagnosis as DiagnosisCore,
	GroupDiagnosisAction,
	DiagnosisSourceRow,
	ClientDiagnosisCode,
} from '@dxcapture/core';

/** A risk-model group is identified by its (model, group) pair alone. */
export interface RiskModelGroupKey {
	readonly model: HccVersions;
	readonly group: string;
}

export interface OptionalIcd {
	readonly id: string;
	readonly description: string;
	readonly riskModelsData: RiskModelData;
	readonly snomedCode: string;
	readonly clientDiagnosisCodes?: ReadonlyArray<ClientDiagnosisCode>;
}

export type DiagnosisAction = Omit<GroupDiagnosisAction, 'isNewAction'>;
export interface Suggestion<T extends GroupMetadata = GroupRiskModelMetadata> extends Omit<
	SuggestionCore,
	'diagnoses'
> {
	readonly group: Group<T> | null;
	readonly diagnoses: ReadonlyArray<Diagnosis>;
}

export interface Diagnosis extends DiagnosisCore {
	readonly originalSavedAction: DiagnosisAction | null;
	readonly originalSavedCode: Diagnosis['code'];
	readonly conditionDuration?: ConditionDuration;
	readonly group?: GroupRiskModel | null;
	/** Agilon/external "bypass" suggestion. Display-only signal to force an open HCC gap. Does NOT affect RAF. */
	readonly isBypassSuggestion?: boolean;
	readonly isForcedHintDueToBypass?: boolean;
	readonly riskModelGroups: ReadonlyArray<RiskModelGroupState>;
}

export type GroupedSuggestions = Array<{ group: Suggestion['group']; suggestions: Suggestion[] }>;

type MainDocumentMetadata = NonNullable<EvidenceSource['document']>;

/** @deprecated -- for backward compatibility only! will be moved to backend once all clients are with new diagnosis card UI
 *
 * use EvidenceSource instead */
export interface EvidenceSourceTableSectionDataRow extends DiagnosisSourceRow {
	readonly dateString: string | null;
}

/** @deprecated -- for backward compatibility only! will be moved to backend once all clients are with new diagnosis card UI
 *
 * use EvidenceSource instead */
export interface EvidenceSourceTableSectionData extends Omit<EvidenceSource['data'][number], 'rows'> {
	readonly rows: ReadonlyArray<EvidenceSourceTableSectionDataRow>;
}

/** @deprecated -- for backward compatibility only! will be moved to backend once all clients are with new diagnosis card UI
 *
 * use EvidenceSource instead */
export interface EvidenceSourceTableSection extends Omit<EvidenceSource, 'data'> {
	readonly data: ReadonlyArray<EvidenceSourceTableSectionData>;
}

export interface DocToView extends MainDocumentMetadata {
	readonly sectionName: string | undefined;
	readonly evidenceOrigin: string;
}

export enum CardRiskModelView {
	card = 'card',
	title = 'title',
	hidden = 'hidden',
}

export enum DxHoldActionView {
	ExtraActions = 'Extra_Actions',
	CardButtons = 'Card_Buttons',
}

export enum DiagnosisGrayoutStrategy {
	OptionalLabelAndGrayOut = 'OptionalLabelAndGrayOut',
	GrayOutOnly = 'GrayOutOnly',
	OptionalLabelOnly = 'OptionalLabelOnly',
	None = 'None',
}

export interface IDiagnosisGrayoutStrategyProvider {
	readonly diagnosisGrayoutStrategy: DiagnosisGrayoutStrategy;
}

export const GRAY_OUT_DISPLAY_MODES: ReadonlyArray<DiagnosisGrayoutStrategy> = [
	DiagnosisGrayoutStrategy.OptionalLabelAndGrayOut,
	DiagnosisGrayoutStrategy.GrayOutOnly,
];

export const OPTIONAL_LABEL_DISPLAY_MODES: ReadonlyArray<DiagnosisGrayoutStrategy> = [
	DiagnosisGrayoutStrategy.OptionalLabelAndGrayOut,
	DiagnosisGrayoutStrategy.OptionalLabelOnly,
];

export interface IAuthModel {
	readonly userView: UserView;
	readonly userFullName: string;
}

export interface SuggestionsGroup<T extends GroupMetadata> {
	group: Group<T>;
	suggestions: ReadonlyArray<Suggestion>;
}

export enum DiagnosisSyncType {
	SyncInEhr = 'Sync-InEhr',
	SyncOutEhr = 'Sync-OutEhr',
}

export enum IcdMappingConfig {
	NoMapping = 'None',
	EdgMapping = 'EDG',
}

export type DxSummaryDisplayMode = 'grouping' | 'standalone';
